import React, {useState, useEffect} from 'react';
import {Helmet} from 'react-helmet';
import {Link} from 'react-router-dom';
import {
    Box,
    Container,
    Card,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    TableFooter,
    TablePagination,
    Divider,
    Typography,
} from '@material-ui/core';
import PerfectScrollbar from 'react-perfect-scrollbar';
import UserListSkelet from '../../skeletons/UserListSkelet';
import {useDelete, useGet} from '../../API/request';
import {useConfirm} from "../../components/Confirm/index";
import {BallTriangle} from "react-loader-spinner";
import '../../styles/All.css'
import useSavePagePagination from '../../hooks/useSavePagePagination';

const DocumentsList = () => {

    const confirm = useConfirm();
    const getU = useGet();
    const deleteU = useDelete();

    // --- Сканы документов ---
    const [isLoaded, setIsLoaded] = useState(true);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [documents, setDocuments] = useState([]);
    const {page, limit, setPage, setLimit} = useSavePagePagination('docs-pagination');
    const [count, setCount] = useState(0);

    // --- Текстовые документы ---
    const [isPersonalDataLoading, setIsPersonalDataLoading] = useState(true);
    const [personalDocuments, setPersonalDocuments] = useState([]);
    const {page: personalPage, limit: personalLimit, setPage: setPersonalPage, setLimit: setPersonalLimit} = useSavePagePagination('personal-docs-pagination');
    const [personalCount, setPersonalCount] = useState(0);

    const formattedDateTime = (createdAt) => {
        if (!createdAt) return '';
        return new Date(createdAt).toLocaleString('ru-RU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    };

    const loadDocuments = () => {
        setIsDataLoading(true);
        getU(`documents?page=${page + 1}&limit=${limit}`)
            .then((resp) => {
                if (resp.status === 'success') {
                    setDocuments(resp.data.document);
                    setCount(resp.data.totalCount || 0);
                }
            })
            .catch((err) => {
                console.log(err.response);
                setDocuments([]);
                setCount(0);
            })
            .finally(() => setIsDataLoading(false));
    };

    const loadPersonalDocuments = () => {
        setIsPersonalDataLoading(true);
        getU(`admin/v1/personal/documents?page=${personalPage + 1}&limit=${personalLimit}`)
            .then((resp) => {
                if (resp.status === 'success') {
                    setPersonalDocuments(resp.data.list || []);
                    setPersonalCount(resp.data.meta?.total || 0);
                }
            })
            .catch((err) => {
                console.log(err.response);
                setPersonalDocuments([]);
                setPersonalCount(0);
            })
            .finally(() => setIsPersonalDataLoading(false));
    };

    const onDelete = (id) => {
        confirm({
            title: 'Удаление',
            content: 'Вы уверены, что хотите удалить документ?',
            onConfirm: () => {
                deleteU(`documents/${id}`)
                    .then((resp) => {
                        if (resp.status === 'success') loadDocuments();
                    })
                    .catch((e) => console.log(e));
            }
        });
    };

    const onDeletePersonal = (id) => {
        confirm({
            title: 'Удаление',
            content: 'Вы уверены, что хотите удалить документ?',
            onConfirm: () => {
                deleteU(`admin/v1/personal/documents/${id}`)
                    .then((resp) => {
                        if (resp.status === 'success') loadPersonalDocuments();
                    })
                    .catch((e) => console.log(e));
            }
        });
    };

    useEffect(() => {
        setIsLoaded(true);
        Promise.all([
            getU(`documents?page=${page + 1}&limit=${limit}`)
                .then((resp) => {
                    if (resp.status === 'success') {
                        setDocuments(resp.data.document);
                        setCount(resp.data.totalCount || 0);
                    }
                })
                .catch((err) => {
                    console.log(err.response);
                    setDocuments([]);
                    setCount(0);
                }),
            getU(`admin/v1/personal/documents?page=${personalPage + 1}&limit=${personalLimit}`)
                .then((resp) => {
                    if (resp.status === 'success') {
                        setPersonalDocuments(resp.data.list || []);
                        setPersonalCount(resp.data.meta?.total || 0);
                    }
                })
                .catch((err) => {
                    console.log(err.response);
                    setPersonalDocuments([]);
                    setPersonalCount(0);
                }),
        ]).finally(() => {
            setIsLoaded(false);
            setIsDataLoading(false);
            setIsPersonalDataLoading(false);
        });
    }, []);

    useEffect(() => {
        loadDocuments();
    }, [page, limit]);

    useEffect(() => {
        loadPersonalDocuments();
    }, [personalPage, personalLimit]);

    if (isLoaded) {
        return (
            <div className="loader">
                <BallTriangle height="100" width="100" color='grey' ariaLabel='loading'/>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Документы</title>
            </Helmet>
            <Box className="headerWrapper">
                <Box className="headerTitle">
                    Документы
                </Box>
            </Box>
            <Box sx={{backgroundColor: 'background.default', minHeight: '100%', py: 3}}>
                <Container maxWidth={false}>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Box sx={{marginLeft: 2}}>
                            <Link to="/app/personal-document/add">
                                <Button color="primary" variant="contained">
                                    Добавить текстовый документ
                                </Button>
                            </Link>
                        </Box>
                        <Box sx={{marginLeft: 2}}>
                            <Link to="/app/document/add">
                                <Button color="primary" variant="contained">
                                    Добавить скан документа
                                </Button>
                            </Link>
                        </Box>
                    </Box>

                    {/* Текстовые документы */}
                    <Box sx={{pt: 3}}>
                        <Typography variant="h6" sx={{mb: 1}}>Текстовые документы</Typography>
                        {isPersonalDataLoading ? <UserListSkelet/> : (
                            <Card>
                                <PerfectScrollbar>
                                    <Box sx={{minWidth: 1000}}>
                                        <Divider/>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{width: 80}}>Id</TableCell>
                                                    <TableCell>Название документа</TableCell>
                                                    <TableCell/>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {personalDocuments?.map((doc) => (
                                                    <TableRow hover key={doc.id}>
                                                        <TableCell sx={{width: 80}}>
                                                            {doc?.id || '---'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {doc?.title || '---'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                                                                <Link to={`/app/personal-document/edit/${doc.id}`}>
                                                                    <Button color="primary" variant="contained">
                                                                        Редакт.
                                                                    </Button>
                                                                </Link>
                                                                <Box sx={{ml: 2}}>
                                                                    <Button
                                                                        color="error"
                                                                        variant="contained"
                                                                        onClick={() => onDeletePersonal(doc.id)}
                                                                    >
                                                                        Удалить
                                                                    </Button>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                            <TableFooter>
                                                <TableRow>
                                                    <TablePagination
                                                        labelRowsPerPage={<span>Кол-во строк на странице:</span>}
                                                        rowsPerPageOptions={[10, 20, 30, 40, 50]}
                                                        colSpan={3}
                                                        count={personalCount}
                                                        rowsPerPage={personalLimit}
                                                        page={personalPage}
                                                        onPageChange={(e, newPage) => setPersonalPage(newPage)}
                                                        onRowsPerPageChange={(e) => {
                                                            setPersonalLimit(e.target.value);
                                                            setPersonalPage(0);
                                                        }}
                                                    />
                                                </TableRow>
                                            </TableFooter>
                                        </Table>
                                    </Box>
                                </PerfectScrollbar>
                            </Card>
                        )}
                    </Box>

                    {/* Сканы документов */}
                    <Box sx={{pt: 3}}>
                        <Typography variant="h6" sx={{mb: 1}}>Сканы документов</Typography>
                        {isDataLoading ? <UserListSkelet/> : (
                            <Card>
                                <PerfectScrollbar>
                                    <Box sx={{minWidth: 1000}}>
                                        <Divider/>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{width: 80}}>Id</TableCell>
                                                    <TableCell>Название документа</TableCell>
                                                    <TableCell>Описание</TableCell>
                                                    <TableCell>Дата получения документа</TableCell>
                                                    <TableCell/>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {documents?.map((document) => (
                                                    <TableRow hover key={document.id}>
                                                        <TableCell sx={{width: 80}}>
                                                            {document?.id || '---'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {document?.title || '---'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {document?.description || '---'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {formattedDateTime(document?.date_receipt) || '---'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                                                                <Link to={`/app/document/${document.id}`}>
                                                                    <Button color="primary" variant="contained">
                                                                        Инфо.
                                                                    </Button>
                                                                </Link>
                                                                <Box sx={{ml: 2}}>
                                                                    <Link to={`/app/document/edit/${document.id}`}>
                                                                        <Button color="primary" variant="contained">
                                                                            Редакт.
                                                                        </Button>
                                                                    </Link>
                                                                </Box>
                                                                <Box sx={{ml: 2}}>
                                                                    <Button
                                                                        color="error"
                                                                        variant="contained"
                                                                        onClick={() => onDelete(document.id)}
                                                                    >
                                                                        Удалить
                                                                    </Button>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                            <TableFooter>
                                                <TableRow>
                                                    <TablePagination
                                                        labelRowsPerPage={<span>Кол-во строк на странице:</span>}
                                                        rowsPerPageOptions={[10, 20, 30, 40, 50]}
                                                        colSpan={6}
                                                        count={count}
                                                        rowsPerPage={limit}
                                                        page={page}
                                                        onPageChange={(e, newPage) => setPage(newPage)}
                                                        onRowsPerPageChange={(e) => {
                                                            setLimit(e.target.value);
                                                            setPage(0);
                                                        }}
                                                    />
                                                </TableRow>
                                            </TableFooter>
                                        </Table>
                                    </Box>
                                </PerfectScrollbar>
                            </Card>
                        )}
                    </Box>
                </Container>
            </Box>
        </>
    );
};

export default DocumentsList;
